import React, { useEffect, useState, useCallback } from 'react';
import { useAutoSave } from '../hooks/useAutoSave';
import AutoSaveIndicator from '../components/ui/AutoSaveIndicator';
import { useTranslation } from 'react-i18next';
import { Save, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import apiService from '../services/api.service';

/**
 * Mon compte — email, nom, mot de passe.
 *
 * Vit HORS des paramètres d'organisation : ces champs sont ceux de la PERSONNE,
 * pas d'une organisation. Un admin qui travaille sur l'organisation d'un client
 * doit pouvoir changer son propre mot de passe sans avoir à en choisir une, et
 * un organisateur ne doit pas chercher son email dans les réglages de sa salle.
 */
const Account: React.FC = () => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  // L'effet de chargement s'exécute avant la création du hook : la ref lui
  // donne accès à `setBaseline` sans réordonner le fichier.
  const baselineRef = React.useRef<((v: Record<string, any>) => void) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState({
    email: '',
    first_name: '',
    last_name: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    apiService
      .getCurrentUser()
      .then((u: any) => {
        setProfile((p) => ({
          ...p,
          email: u?.email || '',
          first_name: u?.first_name || '',
          last_name: u?.last_name || '',
        }));
        // Ce que le serveur porte : `commitIfChanged` s'y compare pour ne pas
        // réécrire un champ qu'on a seulement traversé au clavier.
        baselineRef.current?.({ first_name: u?.first_name || '', last_name: u?.last_name || '' });
      })
      .catch((e) => console.error('Chargement du compte impossible:', e));
  }, []);

  /**
   * Le PROFIL s'enregistre au fur et à mesure : prénom et nom partent au blur.
   * `PUT /users/me` n'écrit que les clés fournies, donc un champ en cours de
   * frappe n'écrase pas l'autre.
   *
   * ⛔ Le MOT DE PASSE garde son bouton, et c'est délibéré : il ne se change
   * pas en quittant un champ. Il exige le mot de passe actuel, la confirmation
   * doit correspondre, et un changement à moitié tapé enverrait un secret que
   * personne n'a voulu poser. Un champ de mot de passe n'a rien d'un réglage.
   */
  const auto = useAutoSave<Record<string, any>>(useCallback(async (patch) => {
    await apiService.updateProfile(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []), {
    onError: (err: any) => setError(err?.response?.data?.detail || t('settings.messages.saveError')),
  });
  baselineRef.current = auto.setBaseline;

  const handleChangePassword = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!profile.new_password) return;
      if (profile.new_password !== profile.confirm_password) {
        setError(t('settings.messages.passwordMismatch'));
        return;
      }
      if (profile.new_password.length < 8) {
        setError(t('settings.messages.passwordTooShort'));
        return;
      }

      // Uniquement les trois champs du bloc : le profil, lui, est déjà écrit.
      await apiService.updateProfile({
        current_password: profile.current_password,
        new_password: profile.new_password,
      });

      setSuccess(t('settings.messages.profileUpdated'));
      setProfile({ ...profile, current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.messages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <p className="text-sm text-muted-foreground">{t('account.title')}</p>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('settings.personalInfo.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('settings.personalInfo.email')}</Label>
                  <Input
                    id="email"
                    autoComplete="username"
                    value={profile.email}
                    disabled
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="first_name">{t('settings.personalInfo.firstName')}</Label>
                  <Input
                    id="first_name"
                    value={profile.first_name}
                    onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                    onBlur={(e) => void auto.commitIfChanged('first_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">{t('settings.personalInfo.lastName')}</Label>
                  <Input
                    id="last_name"
                    value={profile.last_name}
                    onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                    onBlur={(e) => void auto.commitIfChanged('last_name', e.target.value)}
                  />
                </div>
                {/* Le profil s'enregistre tout seul : l'ÉTAT au lieu d'un bouton. */}
                <div className="flex items-center justify-between gap-3 pt-1">
                  <span className="text-xs text-muted-foreground">{t('autoSave.hint')}</span>
                  <AutoSaveIndicator status={auto.status} retry={auto.retry} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('settings.passwordChange.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">{t('settings.passwordChange.currentPassword')}</Label>
                  <Input
                    id="current_password"
                    type="password"
                    autoComplete="current-password"
                    value={profile.current_password}
                    onChange={(e) => setProfile({ ...profile, current_password: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_password">{t('settings.passwordChange.newPassword')}</Label>
                  <Input
                    id="new_password"
                    type="password"
                    autoComplete="new-password"
                    value={profile.new_password}
                    onChange={(e) => setProfile({ ...profile, new_password: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">{t('settings.passwordChange.confirmPassword')}</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    autoComplete="new-password"
                    value={profile.confirm_password}
                    onChange={(e) => setProfile({ ...profile, confirm_password: e.target.value })}
                  />
                </div>
                <div className="pt-2">
                  <Button
                    onClick={handleChangePassword}
                    disabled={saving || !profile.new_password}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {t('settings.passwordChange.updateButton')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
    </div>
  );
};

export default Account;
